<template>
  <div>
    <message :messages="messages"></message>
    <v-card>
      <v-toolbar color="grey darken-1" dark>
        <v-toolbar-title>
          <v-icon large>{{claimIcon}}</v-icon>
          <span v-if="commonForm.appID==='MO'">Motor Claim Summary</span>
          <span v-else-if="commonForm.appID==='TR'">Travel Claim Summary</span>
          <span v-else>Claim Summary</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-divider></v-divider>
      <v-card-text>
        <v-container fluid grid-list-lg>
          <v-layout row wrap>
            <v-flex lg6 xs12>
              <v-container fluid grid-list-lg>
                <v-layout row wrap>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">Claim Reference No.</v-subheader>
                  </v-flex>
                  <v-flex xs6>
                    <span class="title">{{commonForm.clmRefNo}}</span>
                  </v-flex>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">Current Status</v-subheader>
                  </v-flex>
                  <v-flex xs6>
                    <span :class="currentStatusClass">{{currentStatusLabel}}</span>
                  </v-flex>
                  <template v-if="commonForm.lossDate">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Loss Date</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{formatDisplayDateTime2(commonForm.lossDate)}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.notDate">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Notification Date</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{formatDate(commonForm.notDate)}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.ackDate">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Acknowledge Date</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{formatDate(commonForm.ackDate)}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.riskNo">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Policy Benefit Section</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{commonForm.riskNo}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.itemNo">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Item No.</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{commonForm.itemNo}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.cseLoss1">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Cause of Loss</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{commonForm.cseLoss1}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.cseLoss2">
                    <v-flex xs6>
                      <v-subheader class="queryLabel"></v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{commonForm.cseLoss2}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.cseLoss3">
                    <v-flex xs6>
                      <v-subheader class="queryLabel"></v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{commonForm.cseLoss3}}</span>
                    </v-flex>
                  </template>
                </v-layout>
              </v-container>
            </v-flex>
            <v-flex lg6 xs12>
              <v-container fluid grid-list-lg>
                <v-layout row wrap>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">Policy No.</v-subheader>
                  </v-flex>
                  <v-flex xs6>
                    <span class="title">{{commonForm.polNo}}</span>
                  </v-flex>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">Proposal No.</v-subheader>
                  </v-flex>
                  <v-flex xs6>
                    <span class="title">{{commonForm.proposalNo}}</span>
                  </v-flex>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">Intermediary</v-subheader>
                  </v-flex>
                  <v-flex xs6>
                    <span>{{commonForm.intermediary}} - {{commonInfo.intermediaryName}}</span>
                  </v-flex>
                  <template v-if="commonForm.startDate && commonForm.endDate">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Period of Cover</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{formatDate(commonForm.startDate)}} - {{formatDate(commonForm.endDate)}}</span>
                    </v-flex>
                  </template>
                  <template v-if="commonForm.acceptDate">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Acceptance Date</v-subheader>
                    </v-flex>
                    <v-flex xs6>
                      <span>{{formatDisplayDateTime2(commonForm.acceptDate)}}</span>
                    </v-flex>
                  </template>
                </v-layout>
              </v-container>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { formatDisplayDate, formatDisplayDateTime2 } from '@/utils/date'
import { formatDisplayNumber } from '@/utils/number'
import { appIcon } from '@/const/underwriting'
import { claimStatus } from '@/const/claims'
import MessageMixin from '@/mixins/message'
import { mapGetters } from 'vuex'

export default {
  computed: {
    ...mapGetters([
      'checkAccess'
    ]),
    claimDetail() {
      return this.$store.state.claims.claimDetail
    },
    currentStatusLabel() {
      if (claimStatus[this.claimDetail.commonInfo.statusCode]) {
        return claimStatus[this.claimDetail.commonInfo.statusCode]
      } else {
        return 'Unknown Status'
      }
    },
    currentStatusClass() {
      if (this.claimDetail.commonInfo.statusCode === 'A' || this.claimDetail.commonInfo.statusCode === 'S') {
        return 'success--text'
      } else if (this.claimDetail.commonInfo.statusCode === 'X' || this.claimDetail.commonInfo.statusCode === 'V') {
        return 'error--text'
      } else {
        return ''
      }
    },
    claimForm() {
      return this.claimDetail.claimForm
    },
    commonForm() {
      return this.claimDetail.commonForm
    },
    commonInfo() {
      return this.claimDetail.commonInfo
    },
    claimIcon() {
      if (this.commonForm.appID === 'MO') {
        return appIcon.MOTOR
      } else if (this.commonForm.appID === 'TR') {
        return appIcon.TRAVEL
      } else {
        return 'mdi-cash-usd'
      }
    }
  },
  methods: {
    formatDate(date) {
      return formatDisplayDate(date)
    },
    formatDisplayDateTime2(date) {
      return formatDisplayDateTime2(date)
    },
    formatDisplayNumber(xNum) {
      return formatDisplayNumber(xNum)
    }
  },
  mixins: [
    MessageMixin
  ]
}
</script>

<style lang="scss">
</style>
